import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import HamburgerMenu from './HamburgerMenu';
import { useAuth } from './useAuth';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
        success: isDark ? '#4caf50' : '#28a745',
        danger: isDark ? '#f44336' : '#dc3545',
    };

    return {
        page: { backgroundColor: colors.bg, minHeight: '100vh', padding: '20px' },
        container: { maxWidth: '1200px', margin: '0 auto', fontFamily: 'sans-serif' },
        pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
        pageTitle: { fontSize: '24px', margin: 0, color: colors.text },
        card: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            padding: '20px',
            boxShadow: `0 2px 4px ${colors.shadow}`,
            marginBottom: '20px'
        },
        cardTitle: { fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: colors.text },
        formGroup: { marginBottom: '20px' },
        label: {
            display: 'block',
            marginBottom: '8px',
            fontWeight: 'bold',
            color: colors.text,
            fontSize: '14px'
        },
        input: {
            width: '100%',
            maxWidth: '200px',
            padding: '8px 12px',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            backgroundColor: isDark ? '#2a2a2a' : '#fff',
            color: colors.text,
            fontSize: '14px'
        },
        button: {
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            marginRight: '10px'
        },
        buttonPrimary: {
            backgroundColor: colors.link,
            color: 'white'
        },
        buttonSecondary: {
            backgroundColor: '#6c757d',
            color: 'white'
        },
        section: {
            marginBottom: '30px',
            paddingBottom: '20px',
            borderBottom: `1px solid ${colors.border}`
        },
        sectionTitle: {
            fontSize: '16px',
            fontWeight: 'bold',
            marginBottom: '15px',
            color: colors.text
        },
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '15px'
        },
        helpText: {
            fontSize: '12px',
            color: colors.subtleText,
            fontStyle: 'italic',
            marginTop: '4px'
        }
    };
};

function AMPTAdminPage({ theme }) {
    const styles = getStyles(theme);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [updatingACI, setUpdatingACI] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [showRawData, setShowRawData] = useState(false);
    const navigate = useNavigate();
    const { isStaff } = useAuth();
    
    // Check if user has permission (Staff only)
    useEffect(() => {
        if (!isStaff) {
            setAlert({ show: true, message: 'Access denied. Staff access required.', type: 'error' });
            setTimeout(() => navigate('/'), 2000);
        }
    }, [isStaff, navigate]);

    // Fetch configuration - only run once on mount
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                setLoading(true);
                const response = await api.get('/ampt-configuration/');
                // API returns array, get first item
                const configData = Array.isArray(response.data) ? response.data[0] : response.data;
                
                // Check if configData is null or undefined
                if (!configData) {
                    console.error('AMPT configuration is null or undefined');
                    setAlert({ 
                        show: true, 
                        message: 'Configuration not found. The database may need to be migrated. Please contact an administrator.', 
                        type: 'error' 
                    });
                    return;
                }
                
                setConfig(configData);
            } catch (err) {
                console.error('Error fetching AMPT configuration:', err);
                console.error('Error details:', err.response?.data);
                if (err.response && err.response.status === 401) {
                    navigate('/login');
                } else if (err.response && err.response.status === 500) {
                    setAlert({ 
                        show: true, 
                        message: 'Server error. The database may need to be migrated. Please contact an administrator.', 
                        type: 'error' 
                    });
                } else {
                    setAlert({ 
                        show: true, 
                        message: `Failed to load configuration: ${err.response?.data?.detail || err.message || 'Unknown error'}. Please try again or contact an administrator.`, 
                        type: 'error' 
                    });
                }
            } finally {
                setLoading(false);
            }
        };

        if (isStaff) {
            fetchConfig();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    const handleChange = (field, value) => {
        setConfig(prev => ({
            ...prev,
            [field]: parseFloat(value) || 0
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const configId = config.id || 1;
            await api.put(`/ampt-configuration/${configId}/`, config);
            setAlert({ show: true, message: 'Configuration saved successfully!', type: 'success' });
        } catch (err) {
            console.error('Error saving configuration:', err);
            if (err.response && err.response.status === 401) {
                navigate('/login');
            } else {
                setAlert({ show: true, message: 'Failed to save configuration. Please try again.', type: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm('Are you sure you want to reset all values to defaults? This cannot be undone.')) {
            return;
        }

        try {
            setSaving(true);
            const defaults = {
                status_percentage: 43.00,
                status_multiplier_PM: 1.00,
                status_multiplier_DN: 1.00,
                status_multiplier_OTH: 0.60,
                status_multiplier_AIMD: 0.60,
                status_multiplier_REI: 0.60,
                status_multiplier_XC: 0.20,
                status_multiplier_AWP: 0.40,
                status_multiplier_DS: 0.40,
                ak0_percentage: 27.00,
                ak0_multiplier_0: 1.00,
                ak0_multiplier_1: 0.75,
                ak0_multiplier_2: 0.50,
                ak0_multiplier_3: 0.25,
                ak0_multiplier_4_plus: 0.00,
                pm_percentage: 5.00,
                pm_multiplier_0: 1.00,
                pm_multiplier_10: 0.90,
                pm_multiplier_15: 0.75,
                pm_multiplier_20: 0.50,
                pm_multiplier_30: 0.25,
                edd_priority_0_5_days: 10.00,
                edd_priority_6_30_days: 6.00,
                edd_priority_31_60_days: 4.00,
                edd_priority_61_plus_days: 2.00,
                ecd_priority_base: 3.00,
                ecd_multiplier_6_30: 0.75,
                ecd_multiplier_31_60: 0.50,
                ecd_multiplier_61_90: 0.25,
                lfd_priority_base: 2.00,
                lfd_multiplier_61_90: 0.75,
                lfd_multiplier_31_60: 0.50,
                lfd_multiplier_5_30: 0.25,
                aci_priority_base: 10.00,
                aci_multiplier_0_30: 0.999,
                aci_multiplier_31_60: 0.666,
                aci_multiplier_61_90: 0.333,
            };
            
            const configId = config.id || 1;
            await api.put(`/ampt-configuration/${configId}/`, { ...config, ...defaults });
            setConfig(prev => ({ ...prev, ...defaults }));
            setAlert({ show: true, message: 'Configuration reset to defaults!', type: 'success' });
        } catch (err) {
            console.error('Error resetting configuration:', err);
            setAlert({ show: true, message: 'Failed to reset configuration. Please try again.', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // Function to update overdue ACIs
    const handleUpdateOverdueACI = useCallback(async () => {
        if (!window.confirm('Are you sure you want to update all overdue ACIs? This will reset them to 87 months from today. This action cannot be undone.')) {
            return;
        }

        try {
            setUpdatingACI(true);
            
            const response = await api.post('/update-overdue-aci/');
            
            if (response.data.status === 'success') {
                setAlert({ 
                    show: true, 
                    message: `Successfully updated ${response.data.updated_count} overdue ACI reports. ${response.data.skipped_count} were skipped.`, 
                    type: 'success' 
                });
                setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
            } else {
                setAlert({ 
                    show: true, 
                    message: response.data.message || 'Failed to update overdue ACIs', 
                    type: 'error' 
                });
                setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
            }
        } catch (err) {
            console.error('Error updating overdue ACIs:', err);
            setAlert({ 
                show: true, 
                message: err.response?.data?.message || 'Failed to update overdue ACIs. Please try again.', 
                type: 'error' 
            });
            setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
        } finally {
            setUpdatingACI(false);
        }
    }, []);

    if (!isStaff) {
        return null;
    }

    if (loading) {
        return (
            <div style={styles.page}>
                <div style={styles.container}>
                    <div style={styles.pageHeader}>
                        <HamburgerMenu theme={theme} />
                        <h1 style={styles.pageTitle}>AMPT Configuration</h1>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                        <Spinner />
                    </div>
                </div>
            </div>
        );
    }

    if (!config) {
        return (
            <div style={styles.page}>
                <div style={styles.container}>
                    <div style={styles.pageHeader}>
                        <HamburgerMenu theme={theme} />
                        <h1 style={styles.pageTitle}>AMPT Configuration</h1>
                    </div>
                    <div style={styles.card}>
                        <p style={{ color: styles.text }}>No configuration found. Please contact an administrator.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />
                
                <div style={styles.pageHeader}>
                    <HamburgerMenu theme={theme} />
                    <h1 style={styles.pageTitle}>AMPT Configuration</h1>
                    <div style={{ width: '20px' }}></div>
                </div>

                <div style={styles.card}>
                    <h2 style={styles.cardTitle}>Priority Percentage Weights</h2>
                    <p style={{ color: styles.subtleText, marginBottom: '20px' }}>
                        Configure the percentage weights used in the Aircraft Maintenance Priority Tool calculations.
                        Changes will affect priority calculations for all aircraft.
                    </p>

                    {/* Raw Database Data Viewer */}
                    <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: `1px solid ${styles.border}` }}>
                        <button
                            onClick={() => setShowRawData(!showRawData)}
                            style={{
                                ...styles.button,
                                ...styles.buttonSecondary,
                                marginBottom: '10px',
                                fontSize: '12px',
                                padding: '6px 12px'
                            }}
                        >
                            {showRawData ? '▼ Hide' : '▶ Show'} Raw Database Data
                        </button>
                        {showRawData && config && (
                            <div style={{
                                backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f8f9fa',
                                padding: '15px',
                                borderRadius: '4px',
                                border: `1px solid ${styles.border}`,
                                fontFamily: 'monospace',
                                fontSize: '12px',
                                overflowX: 'auto',
                                maxHeight: '400px',
                                overflowY: 'auto'
                            }}>
                                <pre style={{
                                    margin: 0,
                                    color: styles.text,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                    {JSON.stringify(config, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>

                    {/* Main Percentage Weights */}
                    <div style={styles.section}>
                        <h3 style={styles.sectionTitle}>Main Percentage Weights</h3>
                        <div style={styles.grid}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    Status Percentage Base (%)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={config.status_percentage || 43.00}
                                    onChange={(e) => handleChange('status_percentage', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>Base weight for status-based priority (default: 43%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    AK0 Percentage Base (%)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={config.ak0_percentage || 27.00}
                                    onChange={(e) => handleChange('ak0_percentage', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>Base weight for AK0 count priority (default: 27%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    PM Percentage Base (%)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={config.pm_percentage || 5.00}
                                    onChange={(e) => handleChange('pm_percentage', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>Base weight for PM percentage priority (default: 5%)</div>
                            </div>
                        </div>
                    </div>

                    {/* PM Percentage Remaining Multipliers */}
                    <div style={styles.section}>
                        <h3 style={styles.sectionTitle}>Planned Maintenance Multipliers (percentage of PM Percentage Base)</h3>
                        <p style={{ color: styles.subtleText, marginBottom: '15px', fontSize: '13px' }}>
                            Configure multipliers based on the percentage remaining until the nearest PM inspection is due. Each multiplier determines what percentage of the PM Percentage Base that remaining percentage receives.
                            Example: If PM Percentage Base is 5% and 10% remaining multiplier is 0.90, 10% remaining gets 5% × 0.90 = 4.5%.
                        </p>
                        <div style={styles.grid}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>PM 0% Remaining Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.pm_multiplier_0 || 1.00}
                                    onChange={(e) => handleChange('pm_multiplier_0', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>0% remaining gets {((config.pm_percentage || 5) * (config.pm_multiplier_0 || 1)).toFixed(2)}% (default: 1.00 = 100%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>PM 10% Remaining Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.pm_multiplier_10 || 0.90}
                                    onChange={(e) => handleChange('pm_multiplier_10', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>10% remaining gets {((config.pm_percentage || 5) * (config.pm_multiplier_10 || 0.9)).toFixed(2)}% (default: 0.90 = 90%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>PM 15% Remaining Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.pm_multiplier_15 || 0.75}
                                    onChange={(e) => handleChange('pm_multiplier_15', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>15% remaining gets {((config.pm_percentage || 5) * (config.pm_multiplier_15 || 0.75)).toFixed(2)}% (default: 0.75 = 75%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>PM 20% Remaining Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.pm_multiplier_20 || 0.50}
                                    onChange={(e) => handleChange('pm_multiplier_20', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>20% remaining gets {((config.pm_percentage || 5) * (config.pm_multiplier_20 || 0.5)).toFixed(2)}% (default: 0.50 = 50%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>PM 30% Remaining Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.pm_multiplier_30 || 0.25}
                                    onChange={(e) => handleChange('pm_multiplier_30', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>30% remaining gets {((config.pm_percentage || 5) * (config.pm_multiplier_30 || 0.25)).toFixed(2)}% (default: 0.25 = 25%)</div>
                            </div>
                        </div>
                    </div>

                    {/* Status Multipliers */}
                    <div style={styles.section}>
                        <h3 style={styles.sectionTitle}>Status Multipliers (percentage of Status Percentage Base)</h3>
                        <p style={{ color: styles.subtleText, marginBottom: '15px', fontSize: '13px' }}>
                            Configure multipliers for each status type. Each multiplier determines what percentage of the Status Percentage Base that status receives.
                            Example: If Status Percentage Base is 43% and PM multiplier is 1.00, PM status gets 43% × 1.00 = 43%.
                        </p>
                        <div style={styles.grid}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>PM Status Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.status_multiplier_PM || 1.00}
                                    onChange={(e) => handleChange('status_multiplier_PM', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>PM gets {((config.status_percentage || 43) * (config.status_multiplier_PM || 1)).toFixed(2)}% (default: 1.00 = 100%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>DN Status Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.status_multiplier_DN || 1.00}
                                    onChange={(e) => handleChange('status_multiplier_DN', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>DN gets {((config.status_percentage || 43) * (config.status_multiplier_DN || 1)).toFixed(2)}% (default: 1.00 = 100%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>OTH Status Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.status_multiplier_OTH || 0.60}
                                    onChange={(e) => handleChange('status_multiplier_OTH', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>OTH gets {((config.status_percentage || 43) * (config.status_multiplier_OTH || 0.6)).toFixed(2)}% (default: 0.60 = 60%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>AIMD Status Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.status_multiplier_AIMD || 0.60}
                                    onChange={(e) => handleChange('status_multiplier_AIMD', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>AIMD gets {((config.status_percentage || 43) * (config.status_multiplier_AIMD || 0.6)).toFixed(2)}% (default: 0.60 = 60%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>REI Status Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.status_multiplier_REI || 0.60}
                                    onChange={(e) => handleChange('status_multiplier_REI', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>REI gets {((config.status_percentage || 43) * (config.status_multiplier_REI || 0.6)).toFixed(2)}% (default: 0.60 = 60%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>XC Status Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.status_multiplier_XC || 0.20}
                                    onChange={(e) => handleChange('status_multiplier_XC', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>XC gets {((config.status_percentage || 43) * (config.status_multiplier_XC || 0.2)).toFixed(2)}% (default: 0.20 = 20%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>AWP Status Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.status_multiplier_AWP || 0.40}
                                    onChange={(e) => handleChange('status_multiplier_AWP', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>AWP gets {((config.status_percentage || 43) * (config.status_multiplier_AWP || 0.4)).toFixed(2)}% (default: 0.40 = 40%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>DS Status Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.status_multiplier_DS || 0.40}
                                    onChange={(e) => handleChange('status_multiplier_DS', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>DS gets {((config.status_percentage || 43) * (config.status_multiplier_DS || 0.4)).toFixed(2)}% (default: 0.40 = 40%)</div>
                            </div>
                        </div>
                    </div>

                    {/* AK0 Count Multipliers */}
                    <div style={styles.section}>
                        <h3 style={styles.sectionTitle}>AK0 Count Multipliers (percentage of AK0 Percentage Base)</h3>
                        <p style={{ color: styles.subtleText, marginBottom: '15px', fontSize: '13px' }}>
                            Configure multipliers for each AK0 count. Each multiplier determines what percentage of the AK0 Percentage Base that count receives.
                            Example: If AK0 Percentage Base is 27% and AK0 count 0 multiplier is 1.00, AK0 count 0 gets 27% × 1.00 = 27%.
                        </p>
                        <div style={styles.grid}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>AK0 Count 0 Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.ak0_multiplier_0 || 1.00}
                                    onChange={(e) => handleChange('ak0_multiplier_0', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>AK0 count 0 gets {((config.ak0_percentage || 27) * (config.ak0_multiplier_0 || 1)).toFixed(2)}% (default: 1.00 = 100%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>AK0 Count 1 Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.ak0_multiplier_1 || 0.75}
                                    onChange={(e) => handleChange('ak0_multiplier_1', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>AK0 count 1 gets {((config.ak0_percentage || 27) * (config.ak0_multiplier_1 || 0.75)).toFixed(2)}% (default: 0.75 = 75%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>AK0 Count 2 Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.ak0_multiplier_2 || 0.50}
                                    onChange={(e) => handleChange('ak0_multiplier_2', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>AK0 count 2 gets {((config.ak0_percentage || 27) * (config.ak0_multiplier_2 || 0.5)).toFixed(2)}% (default: 0.50 = 50%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>AK0 Count 3 Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.ak0_multiplier_3 || 0.25}
                                    onChange={(e) => handleChange('ak0_multiplier_3', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>AK0 count 3 gets {((config.ak0_percentage || 27) * (config.ak0_multiplier_3 || 0.25)).toFixed(2)}% (default: 0.25 = 25%)</div>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>AK0 Count 4+ Multiplier</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.ak0_multiplier_4_plus || 0.00}
                                    onChange={(e) => handleChange('ak0_multiplier_4_plus', e.target.value)}
                                    style={styles.input}
                                />
                                <div style={styles.helpText}>AK0 count 4+ gets {((config.ak0_percentage || 27) * (config.ak0_multiplier_4_plus || 0)).toFixed(2)}% (default: 0.00 = 0%)</div>
                            </div>
                        </div>
                    </div>

                    {/* EDD Priority Values */}
                    <div style={styles.section}>
                        <h3 style={styles.sectionTitle}>EDD Priority Values (for AWP status only)</h3>
                        <div style={styles.grid}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>EDD 0-5 Days (%)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={config.edd_priority_0_5_days || 10.00}
                                    onChange={(e) => handleChange('edd_priority_0_5_days', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>EDD 6-30 Days (%)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={config.edd_priority_6_30_days || 6.00}
                                    onChange={(e) => handleChange('edd_priority_6_30_days', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>EDD 31-60 Days (%)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={config.edd_priority_31_60_days || 4.00}
                                    onChange={(e) => handleChange('edd_priority_31_60_days', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>EDD 61+ Days (%)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={config.edd_priority_61_plus_days || 2.00}
                                    onChange={(e) => handleChange('edd_priority_61_plus_days', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ECD Priority */}
                    <div style={styles.section}>
                        <h3 style={styles.sectionTitle}>ECD Priority Base and Multipliers</h3>
                        <div style={styles.grid}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>ECD Base (%)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={config.ecd_priority_base || 3.00}
                                    onChange={(e) => handleChange('ecd_priority_base', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>ECD Multiplier 6-30 Days</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.ecd_multiplier_6_30 || 0.75}
                                    onChange={(e) => handleChange('ecd_multiplier_6_30', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>ECD Multiplier 31-60 Days</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.ecd_multiplier_31_60 || 0.50}
                                    onChange={(e) => handleChange('ecd_multiplier_31_60', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>ECD Multiplier 61-90 Days</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.ecd_multiplier_61_90 || 0.25}
                                    onChange={(e) => handleChange('ecd_multiplier_61_90', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                        </div>
                    </div>

                    {/* LFD Priority */}
                    <div style={styles.section}>
                        <h3 style={styles.sectionTitle}>LFD Priority Base and Multipliers</h3>
                        <div style={styles.grid}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>LFD Base (%)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={config.lfd_priority_base || 2.00}
                                    onChange={(e) => handleChange('lfd_priority_base', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>LFD Multiplier 61-90 Days</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.lfd_multiplier_61_90 || 0.75}
                                    onChange={(e) => handleChange('lfd_multiplier_61_90', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>LFD Multiplier 31-60 Days</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.lfd_multiplier_31_60 || 0.50}
                                    onChange={(e) => handleChange('lfd_multiplier_31_60', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>LFD Multiplier 5-30 Days</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    value={config.lfd_multiplier_5_30 || 0.25}
                                    onChange={(e) => handleChange('lfd_multiplier_5_30', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ACI Priority */}
                    <div style={styles.section}>
                        <h3 style={styles.sectionTitle}>ACI Priority Base and Multipliers</h3>
                        <div style={styles.grid}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>ACI Base (%)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={config.aci_priority_base || 10.00}
                                    onChange={(e) => handleChange('aci_priority_base', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>ACI Multiplier 0-30 Days</label>
                                <input
                                    type="number"
                                    step="0.001"
                                    min="0"
                                    max="1"
                                    value={config.aci_multiplier_0_30 || 0.999}
                                    onChange={(e) => handleChange('aci_multiplier_0_30', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>ACI Multiplier 31-60 Days</label>
                                <input
                                    type="number"
                                    step="0.001"
                                    min="0"
                                    max="1"
                                    value={config.aci_multiplier_31_60 || 0.666}
                                    onChange={(e) => handleChange('aci_multiplier_31_60', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>ACI Multiplier 61-90 Days</label>
                                <input
                                    type="number"
                                    step="0.001"
                                    min="0"
                                    max="1"
                                    value={config.aci_multiplier_61_90 || 0.333}
                                    onChange={(e) => handleChange('aci_multiplier_61_90', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '30px', paddingTop: '20px', borderTop: `1px solid ${styles.border}` }}>
                        <button
                            onClick={handleSave}
                            disabled={saving || updatingACI}
                            style={{ ...styles.button, ...styles.buttonPrimary }}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                            onClick={handleReset}
                            disabled={saving || updatingACI}
                            style={{ ...styles.button, ...styles.buttonSecondary }}
                        >
                            Reset to Defaults
                        </button>
                        <button
                            onClick={handleUpdateOverdueACI}
                            disabled={saving || updatingACI}
                            style={{ 
                                ...styles.button, 
                                backgroundColor: updatingACI ? (theme === 'dark' ? '#555' : '#ccc') : (theme === 'dark' ? '#4a5568' : '#dc3545'),
                                color: 'white',
                                cursor: (saving || updatingACI) ? 'not-allowed' : 'pointer'
                            }}
                            title="Update all overdue ACIs (reset to 87 months from today)"
                        >
                            {updatingACI ? 'Updating ACIs...' : '🔄 Update Overdue ACIs'}
                        </button>
                    </div>

                    {config.updated_at && (
                        <div style={{ marginTop: '15px', fontSize: '12px', color: styles.subtleText }}>
                            Last updated: {new Date(config.updated_at).toLocaleString()} 
                            {config.updated_by && ` by ${config.updated_by}`}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AMPTAdminPage;

